import { MAX_LOCAL_ARCHIVE_SIZE } from "../constants";
import type { StoredPdf } from "../types";
import { bookType } from "./book";

const DB_NAME = "vibetranslations-library";
const STORE_NAME = "books";
const DB_VERSION = 1;

type BookRecord = StoredPdf & { blob: Blob };

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transaction<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const request = operation(tx.objectStore(STORE_NAME));
        let result: T;
        request.onsuccess = () => {
          result = request.result;
        };
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

export async function listLocalBooks(): Promise<StoredPdf[]> {
  const records = await transaction<BookRecord[]>("readonly", (store) =>
    store.getAll(),
  );
  return records
    .map(({ blob: _blob, ...document }) => document)
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}

export async function saveLocalBook(file: File): Promise<string> {
  const documents = await listLocalBooks();
  const used = documents.reduce((total, document) => total + document.size, 0);
  if (used + file.size > MAX_LOCAL_ARCHIVE_SIZE) {
    throw new Error("Yerel arşiv 9 GB güvenlik sınırını aşamaz.");
  }

  const estimate = await navigator.storage?.estimate?.();
  if (
    estimate?.quota &&
    estimate.usage &&
    estimate.usage + file.size > estimate.quota * 0.9
  ) {
    throw new Error("Cihazındaki tarayıcı depolama alanı bu kitap için yetersiz.");
  }
  await navigator.storage?.persist?.();

  const type = bookType(file);
  const id = `${Date.now()}-${crypto.randomUUID()}.${type}`;
  const record: BookRecord = {
    id,
    name: file.name.slice(0, 180),
    path: id,
    size: file.size,
    uploadedAt: new Date().toISOString(),
    type,
    lastPage: 1,
    blob: file,
  };
  await transaction<IDBValidKey>("readwrite", (store) => store.put(record));
  return id;
}

export async function getLocalBook(document: StoredPdf): Promise<File> {
  const record = await transaction<BookRecord | undefined>("readonly", (store) =>
    store.get(document.id),
  );
  if (!record) throw new Error("Kitap yerel arşivde bulunamadı.");
  const mime = record.type === "epub" ? "application/epub+zip" : "application/pdf";
  return new File([record.blob], record.name, { type: mime });
}

export async function deleteLocalBook(id: string): Promise<void> {
  await transaction<undefined>("readwrite", (store) => store.delete(id));
}

export async function updateLocalBookProgress(id: string, lastPage: number): Promise<void> {
  const record = await transaction<BookRecord | undefined>("readonly", (store) =>
    store.get(id),
  );
  if (!record) return;
  record.lastPage = Math.max(1, Math.floor(lastPage));
  await transaction<IDBValidKey>("readwrite", (store) => store.put(record));
}
